import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { map } from 'lodash/fp';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Service } from '@mews-commander/data-access';
import { ActionType, RouteName, TextKey } from '@mews-commander/enums';
import {
    ApplicationState,
    CommanderThunkDispatch,
    WithCompanyId,
    WithEnterpriseId,
    WithScope,
} from '@mews-commander/types';
import { Banner, IconName, Stack, Tab } from '@optimus-web/core';
import { compactArray } from '@mews/fp';
import { AsyncMetaState, LocalizedText } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { Optional } from '@mews/std';
import { asyncContainerFactory, ContentContainer, ModalWrapper, NewModalBodyWrapper } from '@mews-commander/ui';
import { getLocalizedCurrentEnterpriseProductServices } from '@mews-commander/legacy-entities/src/selectors/services';
import { hasCompanyClosedBill } from '@mews-commander/legacy-entities/src/ducks/bills';
import { getCompanyName } from '@mews-commander/legacy-entities/src/ducks';
import { hasReadOnlyPrivilegesForCompanyData } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { changeTab, deleteCompany, initCompanyDetail } from '../actions';
import { CompanyDetailTabs } from '../enums';
import { hasInitFailed, isCompanyDetailInitialized, isInitFetching } from '../selectors';
import { useCloseButton } from '../../../core/windowManager/hooks';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { CompanyPaymentsTab } from '../../payment/components/CompanyPaymentsTab';
import { DetailsTab } from './Tabs/Details';
import { DashboardTab } from './Tabs/Dashboard';
import { ContractTab } from './Tabs/Contract';
import { BillingTab } from './Tabs/Billing';
import { AdditionalInfoTab } from './Tabs/AdditionalInfo';
import { ActionLogTab } from './Tabs/ActionLog';
import { RelatedContacts } from './Tabs/RelatedContacts';

const concatObjectsToArray = (...objects: Array<HeadingAction | null>) =>
    pipe(
        objects,
        array.map((obj) => option.fromNullable(obj)),
        array.compact
    );

interface DispatchProps {
    handleTabClick: (valueTab: CompanyDetailTabs) => void;
    init: () => void;
    deleteAccount: () => void;
    fetchServiceOrders: () => void;
}

interface StateProps extends WithEnterpriseId {
    activeTab: CompanyDetailTabs | undefined;
    isInitialized: boolean | undefined;
    readOnlyPrivilege: boolean;
    hasClosedBill: boolean;
    productServices: Array<Service & { localizedName: string }>;
}

interface WithCompanyName {
    companyName: Optional<string>;
}

type CompanyDetailProps = StateProps & DispatchProps & WithScope & WithCompanyId & WithCompanyName;

const getTabComponent = (activeTab: CompanyDetailTabs, scope: string, companyId: string) => {
    switch (activeTab) {
        case CompanyDetailTabs.Dashboard:
            return <DashboardTab id={companyId} />;
        case CompanyDetailTabs.AdditionalInfo:
            return <AdditionalInfoTab id={companyId} />;
        case CompanyDetailTabs.Details:
            return <DetailsTab scope={scope} companyId={companyId} />;
        case CompanyDetailTabs.RelatedContacts:
            return <RelatedContacts scope={scope} companyId={companyId} />;
        case CompanyDetailTabs.ActionLog:
            return <ActionLogTab companyId={companyId} />;
        case CompanyDetailTabs.Contract:
            return <ContractTab scope={scope} companyId={companyId} />;
        case CompanyDetailTabs.Payments:
            return <CompanyPaymentsTab accountId={companyId} />;
        case CompanyDetailTabs.Billing:
            return <BillingTab companyId={companyId} />;
        default:
            return null;
    }
};

const getTabs = () =>
    pipe(
        [
            { textKey: TextKey.Dashboard, value: CompanyDetailTabs.Dashboard },
            { textKey: TextKey.DetailPlural, value: CompanyDetailTabs.Details },
            { textKey: TextKey.AdditionalInfo, value: CompanyDetailTabs.AdditionalInfo },
            { textKey: TextKey.RelatedContacts, value: CompanyDetailTabs.RelatedContacts },
            { textKey: TextKey.Contract, value: CompanyDetailTabs.Contract },
            { textKey: TextKey.PaymentPlural, value: CompanyDetailTabs.Payments },
            { textKey: TextKey.Billing, value: CompanyDetailTabs.Billing },
            { textKey: TextKey.ActionLogItemReport, value: CompanyDetailTabs.ActionLog },
        ],
        compactArray,
        map(({ textKey, value }) => (
            <Tab key={value} id={value} label={<LocalizedText textKey={textKey} />} value={value} />
        ))
    );

const CompanyDetail = ({
    handleTabClick,
    init,
    activeTab,
    scope,
    companyId,
    companyName,
    deleteAccount,
    readOnlyPrivilege,
    hasClosedBill,
    productServices,
    isInitialized,
}: CompanyDetailProps) => {
    useEffect(() => {
        if (!isInitialized) init();
    }, [init, isInitialized]);

    let tabComponent = null;

    const resolveRoute = useResolveRoute();

    const tabs = getTabs();
    if (activeTab) {
        tabComponent = getTabComponent(activeTab, scope, companyId);
    }
    const closeAction = useCloseButton();

    const createProductAction: HeadingAction | null =
        !readOnlyPrivilege && activeTab === CompanyDetailTabs.Dashboard
            ? {
                  icon: IconName.Add,
                  items: pipe(
                      productServices,
                      array.map((service) => ({
                          icon: IconName.Add,
                          text: <>{service.localizedName}</>,
                          type: ActionType.ModalOpener,
                          url: resolveRoute(RouteName.ProductServiceOrderCreate, {
                              query: { ServiceId: service.id, OwnerId: companyId },
                          }),
                      }))
                  ),
                  text: <LocalizedText textKey={TextKey.Create} />,
                  type: ActionType.Dropdown,
              }
            : null;

    const deleteAccountAction: HeadingAction | null =
        !readOnlyPrivilege || hasClosedBill
            ? {
                  actionId: 'deleteAccount',
                  onConfirmation: deleteAccount,
                  type: ActionType.ConfirmationDialog,
                  buttonText: <LocalizedText textKey={TextKey.Delete} />,
                  text: <LocalizedText textKey={TextKey.DeleteAccountMessage} />,
                  title: (
                      <LocalizedText textKey={TextKey.DeleteConfirmation} substitutes={{ entity: companyName || '' }} />
                  ),
                  icon: IconName.Delete,
                  isDanger: true,
              }
            : null;

    const actions = concatObjectsToArray(createProductAction, deleteAccountAction);

    return (
        <>
            <ModalHeader
                title={companyName}
                primaryAction={closeAction}
                actions={actions}
                tabsConfig={{
                    activeTabValue: activeTab,
                    handleTabClick: (_e, valueTab) => handleTabClick(valueTab as CompanyDetailTabs),
                    inline: true,
                    tabs,
                }}
            />
            <NewModalBodyWrapper>
                <ContentContainer condensed>
                    <Stack vertical spacing="200">
                        {readOnlyPrivilege && (
                            <Banner
                                centered
                                variant="warning"
                                primaryText={<LocalizedText textKey={TextKey.ReadOnlyPrivilege} />}
                            />
                        )}
                        {tabComponent}
                    </Stack>
                </ContentContainer>
            </NewModalBodyWrapper>
        </>
    );
};

type StateAsyncMetaProps = AsyncMetaState & StateProps & WithCompanyName;

export default pipe(
    CompanyDetail,
    asyncContainerFactory({ ContentElement: ModalWrapper }),
    connect<StateAsyncMetaProps, DispatchProps, WithScope & WithCompanyId, ApplicationState>(
        // @ts-expect-error getLocalizedCurrentEnterpriseProductServices returns object[]
        createStructuredSelector({
            isFetching: isInitFetching,
            hasFailed: hasInitFailed,
            companyName: (state: ApplicationState, { companyId }: WithCompanyId) =>
                getCompanyName(state, { id: companyId }),
            readOnlyPrivilege: hasReadOnlyPrivilegesForCompanyData,
            hasClosedBill: hasCompanyClosedBill,
            productServices: getLocalizedCurrentEnterpriseProductServices,
            isInitialized: isCompanyDetailInitialized,
        }),
        (dispatch: CommanderThunkDispatch, { scope, companyId }) => ({
            handleTabClick: (valueTab: CompanyDetailTabs) => dispatch(changeTab(valueTab, companyId, scope)),
            deleteAccount: async () => dispatch(deleteCompany(companyId)),
            init: async () => dispatch(initCompanyDetail({ scope, companyId })),
        })
    )
);
